/**
 * Result Classifier
 * TASK-293: Single responsibility - Classify results by meeting importance
 *
 * Classifies search results into MUST_DISCUSS, SHOULD_REVIEW, and CONTEXT categories
 */

import type { SourceAttribution } from '../../types/search.types';
import type { ClassifiedResult, ClassifiedResults, MeetingImportance } from './types';

/**
 * Keywords that indicate high priority (MUST_DISCUSS)
 */
const MUST_DISCUSS_KEYWORDS = [
  'urgent',
  'action required',
  'deadline',
  'expiring',
  'alert',
  'warning',
  'critical',
  'risk',
  'compliance',
  'regulation',
  'legal',
  'tax',
  'significant',
  'major',
  'loss',
  'decline',
  'concern',
  'issue',
  'problem',
];

/**
 * Keywords that indicate medium priority (SHOULD_REVIEW)
 */
const SHOULD_REVIEW_KEYWORDS = [
  'update',
  'change',
  'new',
  'recent',
  'performance',
  'return',
  'growth',
  'opportunity',
  'recommendation',
  'suggestion',
  'consider',
  'review',
  'upcoming',
  'scheduled',
  'meeting',
  'call',
];

/**
 * High relevance score threshold
 */
const HIGH_RELEVANCE_THRESHOLD = 0.8;

/**
 * Medium relevance score threshold
 */
const MEDIUM_RELEVANCE_THRESHOLD = 0.5;

/**
 * Classify a single result by importance
 */
export function classifyResult(source: SourceAttribution): ClassifiedResult {
  const importance = determineImportance(source);
  const reason = generateReason(source, importance);

  return {
    importance,
    reason,
    result: source,
  };
}

/**
 * Classify all results and group by importance
 */
export function classifyResults(sources: SourceAttribution[]): ClassifiedResults {
  const classified = sources.map(classifyResult);

  return {
    mustDiscuss: classified.filter((r) => r.importance === 'MUST_DISCUSS'),
    shouldReview: classified.filter((r) => r.importance === 'SHOULD_REVIEW'),
    context: classified.filter((r) => r.importance === 'CONTEXT'),
  };
}

/**
 * Determine the importance level of a source
 */
function determineImportance(source: SourceAttribution): MeetingImportance {
  const text = `${source.title || ''} ${source.excerpt}`.toLowerCase();

  // Check for MUST_DISCUSS keywords
  if (MUST_DISCUSS_KEYWORDS.some((keyword) => text.includes(keyword))) {
    return 'MUST_DISCUSS';
  }

  // High relevance scores are important
  if (source.relevanceScore >= HIGH_RELEVANCE_THRESHOLD) {
    return 'MUST_DISCUSS';
  }

  // Check for SHOULD_REVIEW keywords
  if (SHOULD_REVIEW_KEYWORDS.some((keyword) => text.includes(keyword))) {
    return 'SHOULD_REVIEW';
  }

  // Medium relevance scores should be reviewed
  if (source.relevanceScore >= MEDIUM_RELEVANCE_THRESHOLD) {
    return 'SHOULD_REVIEW';
  }

  // Everything else is context
  return 'CONTEXT';
}

/**
 * Generate a human-readable reason for the classification
 */
function generateReason(source: SourceAttribution, importance: MeetingImportance): string {
  const text = `${source.title || ''} ${source.excerpt}`.toLowerCase();

  if (importance === 'MUST_DISCUSS') {
    // Find the specific keyword that triggered it
    const keyword = MUST_DISCUSS_KEYWORDS.find((k) => text.includes(k));
    if (keyword) {
      return `Contains "${keyword}" - requires discussion`;
    }
    if (source.relevanceScore >= HIGH_RELEVANCE_THRESHOLD) {
      return `High relevance score (${Math.round(source.relevanceScore * 100)}%)`;
    }
    return 'High priority item';
  }

  if (importance === 'SHOULD_REVIEW') {
    const keyword = SHOULD_REVIEW_KEYWORDS.find((k) => text.includes(k));
    if (keyword) {
      return `Contains "${keyword}" - worth reviewing`;
    }
    if (source.relevanceScore >= MEDIUM_RELEVANCE_THRESHOLD) {
      return `Medium relevance score (${Math.round(source.relevanceScore * 100)}%)`;
    }
    return 'May be relevant to discussion';
  }

  return 'Background information';
}

/**
 * Get counts for each importance level
 */
export function getImportanceCounts(results: ClassifiedResults): {
  mustDiscuss: number;
  shouldReview: number;
  context: number;
  total: number;
} {
  return {
    mustDiscuss: results.mustDiscuss.length,
    shouldReview: results.shouldReview.length,
    context: results.context.length,
    total:
      results.mustDiscuss.length + results.shouldReview.length + results.context.length,
  };
}

/**
 * Rank an importance level for sorting (lower = more important).
 * Uses ?? (not ||) so MUST_DISCUSS (0) is not coerced to the fallback by falsiness.
 */
export function importanceRank(importance: MeetingImportance): number {
  const order: Record<MeetingImportance, number> = {
    MUST_DISCUSS: 0,
    SHOULD_REVIEW: 1,
    CONTEXT: 2,
  };
  return order[importance] ?? 2;
}

/**
 * Sort results within each category by relevance score
 */
export function sortByRelevance(results: ClassifiedResults): ClassifiedResults {
  const sortFn = (a: ClassifiedResult, b: ClassifiedResult) =>
    b.result.relevanceScore - a.result.relevanceScore;

  return {
    mustDiscuss: [...results.mustDiscuss].sort(sortFn),
    shouldReview: [...results.shouldReview].sort(sortFn),
    context: [...results.context].sort(sortFn),
  };
}

/**
 * Filter results to only include items above a minimum relevance
 */
export function filterByMinRelevance(
  results: ClassifiedResults,
  minRelevance: number = 0.3
): ClassifiedResults {
  const filterFn = (r: ClassifiedResult) => r.result.relevanceScore >= minRelevance;

  return {
    mustDiscuss: results.mustDiscuss.filter(filterFn),
    shouldReview: results.shouldReview.filter(filterFn),
    context: results.context.filter(filterFn),
  };
}
